import { useCallback, useEffect, useMemo, useState } from "react";
import { FormHelperText } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import { useNotification } from "@context/NotificationContext";
import { usePortServicesFlow } from "@features/vessel-call/vessel-call-status/PortServices/flow/context/PortServicesFlowContext";
import { useUploadDocuments } from "@features/vessel-call/vessel-call-status/PortServices/hooks/useUploadDocuments";
import { useTranslation } from "react-i18next";

const LANG_BASE = "vessel-call.port-services";
const UPLOAD_KEY = "slopsDisposalDocuments";
const MAX_SLOPS_IMAGES = 25;
const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];

const SlopsDisposalDocuments = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { errors, setErrors, slopsDisposalDocuments, setSlopsDisposalDocuments } =
    usePortServicesFlow();

  const uploadEntries = useUploadStore(
    (state) => state.filesByKey[UPLOAD_KEY] ?? EMPTY_UPLOAD_ENTRIES,
  );
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const updateFilesForKey = useUploadStore((state) => state.updateFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);

  const [uploadError, setUploadError] = useState("");

  const fileProfiles = useFileUploadProfiles();
  const uploadConditions = useMemo(
    () => ({
      format: fileProfiles.portServicesImage.format,
      maxSize: fileProfiles.portServicesImage.maxSizeLabel,
      accept: {},
    }),
    [fileProfiles.portServicesImage.format, fileProfiles.portServicesImage.maxSizeLabel],
  );

  const allowedExtensions = useMemo(
    () =>
      (uploadConditions.format ?? []).map((ext) =>
        ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
      ),
    [uploadConditions.format],
  );

  const uploadDescription = useMemo(
    () =>
      t("upload.additional-documents-upload", {
        extensions: allowedExtensions.join(", "),
        size: uploadConditions.maxSize ?? "",
      }),
    [t, allowedExtensions, uploadConditions.maxSize],
  );

  const validateUploadFiles = useCallback(
    (incomingFiles: File[]) => {
      const existingFilesCount = useUploadStore.getState().getFilesForKey(UPLOAD_KEY).length ?? 0;

      if (existingFilesCount + incomingFiles.length > MAX_SLOPS_IMAGES) {
        return t("form-validation.max-images", { max: MAX_SLOPS_IMAGES });
      }

      return null;
    },
    [t],
  );

  const handleValidationResult = useCallback((message: string | null) => {
    setUploadError(message ?? "");
  }, []);

  const setUploadFiles = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey(UPLOAD_KEY);
        setSlopsDisposalDocuments([]);
        return;
      }

      if (files.length > MAX_SLOPS_IMAGES) {
        setUploadError(t("form-validation.max-images", { max: MAX_SLOPS_IMAGES }));
        return;
      }

      setUploadError("");

      setFilesForKey(UPLOAD_KEY, files, { maxFiles: MAX_SLOPS_IMAGES });
    },
    [clearFilesForKey, setFilesForKey, setSlopsDisposalDocuments, t],
  );

  const clearDocumentErrors = useCallback(() => {
    setUploadError("");
    setErrors({
      ...errors,
      slopsDisposalDocuments: undefined,
      files: undefined,
    });
  }, [errors, setErrors]);

  const handleUploadSuccess = useCallback(
    ({
      documentUrls,
      startIdx,
      files,
    }: {
      documentUrls: string[];
      startIdx: number;
      files: File[];
    }) => {
      clearDocumentErrors();

      updateFilesForKey(UPLOAD_KEY, (prev) => {
        const nextEntries = [...prev];

        documentUrls.forEach((url, offset) => {
          const targetIndex = startIdx + offset;
          const uploadedFile = files[offset];
          const fileName = uploadedFile?.name ?? `document-${targetIndex + 1}`;

          nextEntries[targetIndex] = {
            name: fileName,
            url,
            type: uploadedFile?.type,
          };
        });

        return nextEntries;
      });

      const nextDocuments = [...slopsDisposalDocuments];
      documentUrls.forEach((url, offset) => {
        const targetIndex = startIdx + offset;
        const uploadedFile = files[offset];

        nextDocuments[targetIndex] = {
          documentUrl: url,
          documentName: uploadedFile?.name ?? `document-${targetIndex + 1}`,
          fileType: uploadedFile?.type ?? "image/*",
        };
      });

      setSlopsDisposalDocuments(nextDocuments.filter((document) => !!document?.documentUrl));
    },
    [clearDocumentErrors, slopsDisposalDocuments, setSlopsDisposalDocuments, updateFilesForKey],
  );

  const handleUploadError = useCallback(
    (error: Error, meta: { startIdx: number; count: number; fileNames: string[] }) => {
      setUploadError(t("upload.unable-to-upload"));
      notify({
        title: t("upload.unable-to-upload"),
        description: error?.message ?? t("upload.unable-to-upload"),
        type: "error",
      });

      updateFilesForKey(UPLOAD_KEY, (prev) =>
        prev.filter((_, index) => index < meta.startIdx || index >= meta.startIdx + meta.count),
      );
    },
    [notify, t, updateFilesForKey],
  );

  const uploadDocuments = useUploadDocuments({
    handleUploadSuccess,
    handleUploadError,
  });

  const handleUploadDocuments = useCallback(
    (incomingFiles: File[]) => {
      clearDocumentErrors();

      if (!incomingFiles?.length) return;

      const totalFiles = useUploadStore.getState().getFilesForKey(UPLOAD_KEY).length;
      const startIdx = Math.max(totalFiles - incomingFiles.length, 0);

      uploadDocuments.mutate({
        files: incomingFiles,
        startIdx,
      });
    },
    [clearDocumentErrors, uploadDocuments],
  );

  const removeDocumentAtIndex = useCallback(
    (index: number) => {
      setUploadError("");
      setErrors({
        ...errors,
        slopsDisposalDocuments: undefined,
        files: undefined,
      });
      setSlopsDisposalDocuments(
        slopsDisposalDocuments.filter((_, itemIndex) => itemIndex !== index),
      );
    },
    [errors, setErrors, setSlopsDisposalDocuments, slopsDisposalDocuments],
  );

  useEffect(() => {
    return () => {
      clearFilesForKey(UPLOAD_KEY);
      setSlopsDisposalDocuments([]);
    };
  }, [clearFilesForKey, setSlopsDisposalDocuments]);

  return (
    <div className="flex flex-col gap-2 mb-4" data-testid="port-services-slops-disposal-documents">
      <CustomFormLabel
        data-testid="port-services-slops-disposal-documents-label"
        sx={{ variant: "subtitle2", fontWeight: 600 }}
      >
        {t(`${LANG_BASE}.fields.slops-disposal-images`)}
      </CustomFormLabel>
      <DocumentUpload
        testId="port-services-slops-disposal-documents-upload"
        name={UPLOAD_KEY}
        maxFiles={MAX_SLOPS_IMAGES}
        allowedExtensions={allowedExtensions}
        onUpload={handleUploadDocuments}
        validateDrop={(selectedCount) => {
          if (selectedCount > MAX_SLOPS_IMAGES) {
            return t("form-validation.max-images", { max: MAX_SLOPS_IMAGES });
          }
          return null;
        }}
        validateFiles={validateUploadFiles}
        onValidationResult={handleValidationResult}
        conditions={uploadConditions}
        files={uploadEntries}
        setFiles={setUploadFiles}
        uploadDescription={uploadDescription}
        disabled={uploadDocuments.isLoading}
      />
      {errors.slopsDisposalDocuments && (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>
          {errors.slopsDisposalDocuments}
        </FormHelperText>
      )}
      {uploadError ? (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>{uploadError}</FormHelperText>
      ) : null}
      {!!uploadEntries.length &&
        uploadEntries.map((file, index) => (
          <SingleFile
            key={`${UPLOAD_KEY}-${index}`}
            file={file}
            testId={`port-services-slops-disposal-documents-${index}`}
            fieldName={UPLOAD_KEY}
            currentFileIndex={index}
            setFiles={() => undefined}
            removeFn={() => removeDocumentAtIndex(index)}
            isLoading={uploadDocuments.isLoading && file instanceof File}
          />
        ))}
    </div>
  );
};

export default SlopsDisposalDocuments;
